import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, Skeleton, Container, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpaIcon from '@mui/icons-material/Spa';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import useBehaviorTracking from '../hooks/useBehaviorTracking';
import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';

const fixImageUrl = (url) => {
  return normalizeImageUrl(url, 'Product');
};

// Gemini 2.5 Pro - Handmade color palette: warm, earthy, natural
const C = {
  cream: '#faf5ef',
  warmBrown: '#8b6f47',
  darkBrown: '#5c4033',
  lightBrown: '#c4a882',
  terracotta: '#c67a4b',
  sage: '#8fac8f',
  sageDark: '#6d8a6d',
  linen: '#f3ece0',
  text: '#4a3728',
  textLight: '#7a6655',
  white: '#ffffff',
};

const TemplateHandmade = () => {
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [hotProducts, setHotProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBanner, setCurrentBanner] = useState(0);
  const { addToCart } = useCart();
  const { storeConfig, shippingConfig, categories: dbCategories } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('common');
  useBehaviorTracking();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, newRes, hotRes] = await Promise.all([
          api.get('/products/featured?limit=8'),
          api.get('/products?sort=newest&limit=8'),
          api.get('/products?sort=sales&limit=8')
        ]);
        setFeatured(featuredRes.data || []);
        setNewProducts(newRes.data.items || []);
        setHotProducts(hotRes.data.items || []);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const banners = [
    {
      title: storeConfig.store_name || t('template.handmade_shop'),
      subtitle: storeConfig.slogan || t('template.handmade_slogan'),
      bgImage: '/images/banners/banner-handmade.jpg',
    },
    {
      title: t('template.warm_handmade'),
      subtitle: t('template.warm_handmade_sub'),
    },
    {
      title: t('template.free_shipping_promo'),
      subtitle: t('template.free_shipping_fee', { threshold: shippingConfig?.free_shipping_threshold || 799 }),
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentBanner(p => (p + 1) % banners.length), 6000);
    return () => clearInterval(timer);
  }, [banners.length]);

  // Gemini: borderRadius 4 (16px), warm shadows, 1:1 ratio
  const ProductCard = ({ product }) => (
    <Card sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      bgcolor: C.white, borderRadius: 4, overflow: 'hidden',
      transition: 'all 0.4s ease',
      boxShadow: '0 2px 12px rgba(139,111,71,0.06)',
      '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: '0 16px 40px rgba(139,111,71,0.12)',
        '& .product-img': { transform: 'scale(1.05)' },
      }
    }}>
      <Box sx={{ position: 'relative', pt: '100%', bgcolor: C.linen, cursor: 'pointer', overflow: 'hidden' }}
        onClick={() => navigate(`/products/${product.id}`)}>
        <CardMedia className="product-img" component="img" image={fixImageUrl(product.image_url)} alt={product.name} loading="lazy"
          onError={(e) => onImageErrorSetFallback(e, product.name)}
          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', p: 2, transition: 'transform 0.5s ease' }} />
        {product.quantity <= 10 && product.quantity > 0 && (
          <Chip icon={<FavoriteIcon sx={{ fontSize: 11 }} />} label={t('template.limited_count', { count: product.quantity })} size="small"
            sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'rgba(198,122,75,0.9)', color: C.white, fontWeight: 600, fontSize: '0.65rem', height: 24, borderRadius: 3, '& .MuiChip-icon': { color: C.white } }} />
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Typography variant="body2" sx={{
          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 44,
          color: C.text, fontWeight: 500, cursor: 'pointer', lineHeight: 1.6,
          fontFamily: '"Noto Serif TC", serif',
          '&:hover': { color: C.terracotta }
        }} onClick={() => navigate(`/products/${product.id}`)}>{product.name}</Typography>
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ color: C.terracotta, fontWeight: 700, fontSize: '1.15rem' }}>
            NT$ {product.sale_price ? Math.round(Number(product.sale_price)).toLocaleString() : 0}
          </Typography>
          {product.original_price > product.sale_price && (
            <Typography variant="caption" sx={{ color: '#bbb', textDecoration: 'line-through' }}>
              NT$ {product.original_price ? Math.round(Number(product.original_price)).toLocaleString() : 0}
            </Typography>
          )}
        </Box>
      </CardContent>
      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Button fullWidth variant="contained" startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
          onClick={() => addToCart(product.id)} disabled={product.quantity <= 0}
          sx={{
            bgcolor: C.warmBrown, borderRadius: 3, textTransform: 'none',
            fontWeight: 600, py: 1.1, fontSize: '0.85rem',
            boxShadow: 'none',
            '&:hover': { bgcolor: C.darkBrown, boxShadow: '0 4px 12px rgba(92,64,51,0.25)' },
            '&.Mui-disabled': { bgcolor: '#e0d5c7', color: C.textLight },
          }}>
          {product.quantity > 0 ? t('btn.add_to_cart') : t('template.sold_out')}
        </Button>
      </Box>
    </Card>
  );

  // Gemini: serif heading, terracotta accent line, warm feel
  const SectionTitle = ({ icon, overline, title, accent = C.terracotta }) => (
    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
      {icon && <Box sx={{ color: accent, mb: 1, '& svg': { fontSize: 28 } }}>{icon}</Box>}
      {overline && <Typography variant="caption" sx={{ color: C.textLight, letterSpacing: '0.12em', fontSize: '0.75rem' }}>{overline}</Typography>}
      <Typography variant="h4" sx={{
        fontWeight: 600, mt: 0.5, color: C.text,
        fontFamily: '"Noto Serif TC", serif',
        fontSize: { xs: '1.5rem', md: '2rem' },
      }}>{title}</Typography>
      <Box sx={{ width: 40, height: 2, bgcolor: accent, mx: 'auto', mt: 2, borderRadius: 1 }} />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Gemini: organic category nav with warm tones */}
      <Box sx={{ bgcolor: C.linen, py: 1.5, borderBottom: `1px solid rgba(196,168,130,0.3)`, overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, justifyContent: 'center', minWidth: 'max-content' }}>
            <Chip icon={<SpaIcon sx={{ fontSize: 16 }} />} label={t('template.all_products')} onClick={() => navigate('/products')}
              sx={{ bgcolor: C.warmBrown, color: C.white, fontWeight: 600, borderRadius: 3, '& .MuiChip-icon': { color: C.white } }} />
            {dbCategories.slice(0, 6).map(cat => (
              <Chip key={cat.name} label={cat.name} variant="outlined"
                onClick={() => navigate(`/products?category=${encodeURIComponent(cat.name)}`)}
                sx={{ borderColor: C.lightBrown, color: C.text, borderRadius: 3, '&:hover': { bgcolor: 'rgba(139,111,71,0.06)', borderColor: C.warmBrown } }} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Gemini: 75vh hero with warm natural feel */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', transition: 'transform 0.7s ease', transform: `translateX(-${currentBanner * 100}%)` }}>
          {banners.map((banner, idx) => (
            <Box key={idx} sx={{
              minWidth: '100%', bgcolor: theme.palette.primary.main,
              minHeight: { xs: 220, md: 360 },
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              position: 'relative', px: 3,
              backgroundImage: banner.bgImage ? `url(${banner.bgImage})` : `radial-gradient(circle at 30% 50%, ${theme.palette.secondary.main}4D 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(143,172,143,0.2) 0%, transparent 40%)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}>
              <EmojiNatureIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 28, mb: 2,
                animation: currentBanner === idx ? 'fadeIn 0.8s ease forwards' : 'none', opacity: currentBanner === idx ? 1 : 0 }} />
              <Typography sx={{
                color: C.white, fontWeight: 600,
                fontFamily: '"Noto Serif TC", serif',
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
                textAlign: 'center', lineHeight: 1.2,
                animation: currentBanner === idx ? 'slideUp 0.8s ease 0.2s forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }}>{banner.title}</Typography>
              <Typography variant="body2" sx={{
                color: 'rgba(255,255,255,0.75)', mt: 1.5, letterSpacing: '0.1em', textAlign: 'center',
                fontFamily: '"Noto Serif TC", serif',
                animation: currentBanner === idx ? 'slideUp 0.8s ease 0.4s forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }}>{banner.subtitle}</Typography>
              <Button variant="outlined" onClick={() => navigate('/products')}
                sx={{
                  mt: 3, borderColor: 'rgba(255,255,255,0.5)', color: C.white,
                  borderRadius: 3, px: 4, py: 1, letterSpacing: '0.08em',
                  animation: currentBanner === idx ? 'slideUp 0.8s ease 0.6s forwards' : 'none',
                  opacity: currentBanner === idx ? 1 : 0,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: C.white }
                }}>{t('btn.explore')}</Button>
            </Box>
          ))}
        </Box>
        <IconButton onClick={() => setCurrentBanner(p => (p - 1 + banners.length) % banners.length)}
          sx={{ position: 'absolute', left: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.5)', '&:hover': { color: C.white } }}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton onClick={() => setCurrentBanner(p => (p + 1) % banners.length)}
          sx={{ position: 'absolute', right: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.5)', '&:hover': { color: C.white } }}>
          <ArrowForwardIosIcon />
        </IconButton>
        <Box sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1.5 }}>
          {banners.map((_, i) => (
            <Box key={i} onClick={() => setCurrentBanner(i)}
              sx={{ width: currentBanner === i ? 20 : 8, height: 8, bgcolor: currentBanner === i ? C.white : 'rgba(255,255,255,0.35)', cursor: 'pointer', transition: 'all 0.4s ease', borderRadius: 4 }} />
          ))}
        </Box>
      </Box>

      {/* Gemini: shipping strip with warm brown */}
      <Box sx={{ bgcolor: C.darkBrown, py: 1.8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {[
              { icon: <LocalShippingIcon sx={{ fontSize: 16 }} />, text: t('template.free_shipping_threshold', { threshold: shippingConfig?.free_shipping_threshold || 799 }) },
              { icon: <VolunteerActivismIcon sx={{ fontSize: 16 }} />, text: t('template.handcrafted') },
              { icon: <SpaIcon sx={{ fontSize: 16 }} />, text: t('template.natural_materials') },
              { icon: <AutoAwesomeIcon sx={{ fontSize: 16 }} />, text: t('template.one_of_a_kind') },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'rgba(255,255,255,0.8)' }}>
                  {item.icon}
                  <Typography variant="caption" sx={{ fontWeight: 500, letterSpacing: '0.05em' }}>{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Gemini: Brand story two-column section */}
      <Box sx={{ bgcolor: C.linen, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 30px rgba(139,111,71,0.08)' }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: C.warmBrown, p: { xs: 4, md: 6 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { xs: 200, md: 300 } }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: '0.15em', mb: 2, display: 'block' }}>FROM THE STUDIO</Typography>
                <Typography variant="h5" sx={{
                  color: C.white, fontWeight: 600, lineHeight: 1.8, mb: 3,
                  fontFamily: '"Noto Serif TC", serif',
                  fontSize: { xs: '1.1rem', md: '1.35rem' },
                }}>
                  {storeConfig.description || t('template.handmade_story_default')}
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/products')}
                  sx={{ alignSelf: 'flex-start', borderColor: 'rgba(255,255,255,0.5)', color: C.white, borderRadius: 3, letterSpacing: '0.08em', px: 4, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: C.white } }}>
                  {t('btn.browse_works')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: C.sage, minHeight: { xs: 200, md: 300 },
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', p: 4,
              }}>
                <SpaIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 48, mb: 2 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.15em', fontSize: '0.85rem' }}>HANDCRAFTED WITH LOVE</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Gemini: product sections with generous spacing */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        {/* Featured */}
        <SectionTitle icon={<AutoAwesomeIcon />} overline="HANDPICKED" title={t('template.handpicked')} accent={C.terracotta} />
        <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
          {loading ? Array(8).fill(0).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rectangular" sx={{ pt: '100%', borderRadius: 4 }} />
              <Skeleton width="60%" sx={{ mt: 1.5 }} />
              <Skeleton width="40%" />
            </Grid>
          )) : featured.map(product => (
            <Grid item xs={6} sm={4} md={3} key={product.id}><ProductCard product={product} /></Grid>
          ))}
        </Grid>

        {/* Hot */}
        {hotProducts.length > 0 && (
          <Box sx={{ mt: { xs: 6, md: 10 } }}>
            <SectionTitle icon={<FavoriteIcon />} overline="MOST LOVED" title={t('template.most_loved')} accent={C.terracotta} />
            <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
              {hotProducts.slice(0, 4).map(product => (
                <Grid item xs={6} sm={4} md={3} key={product.id}><ProductCard product={product} /></Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Sage accent section */}
        <Box sx={{ mt: { xs: 6, md: 10 }, p: { xs: 4, md: 6 }, bgcolor: 'rgba(143,172,143,0.08)', borderRadius: 4, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: C.sage, letterSpacing: '0.15em', display: 'block', mb: 1 }}>NATURAL & SUSTAINABLE</Typography>
          <Typography variant="h5" sx={{ color: C.text, fontFamily: '"Noto Serif TC", serif', fontWeight: 600, mb: 1 }}>{t('template.natural_sustainable_title')}</Typography>
          <Typography variant="body2" sx={{ color: C.textLight, maxWidth: 500, mx: 'auto', lineHeight: 1.8 }}>
            {t('template.natural_sustainable_desc')}
          </Typography>
        </Box>

        {/* New */}
        {newProducts.length > 0 && (
          <Box sx={{ mt: { xs: 6, md: 10 } }}>
            <SectionTitle icon={<EmojiNatureIcon />} overline="FRESH FROM STUDIO" title={t('template.new_arrivals')} accent={C.sage} />
            <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
              {newProducts.slice(0, 4).map(product => (
                <Grid item xs={6} sm={4} md={3} key={`new-${product.id}`}><ProductCard product={product} /></Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Brand guarantee */}
        <Box sx={{ mt: { xs: 6, md: 10 }, p: { xs: 4, md: 6 }, bgcolor: C.linen, borderRadius: 4 }}>
          <Grid container spacing={4} justifyContent="center">
            {[
              { icon: <VolunteerActivismIcon />, title: t('template.handcrafted'), desc: t('template.handmade_each'), color: C.terracotta },
              { icon: <LocalShippingIcon />, title: t('template.safe_delivery'), desc: t('template.safe_delivery_desc'), color: C.warmBrown },
              { icon: <SpaIcon />, title: t('template.natural_materials'), desc: t('template.natural_material_desc'), color: C.sage },
              { icon: <FavoriteIcon />, title: t('template.after_sales'), desc: storeConfig.service_hours || t('template.service_hours_short'), color: C.terracotta },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: item.color, mb: 1.5, '& svg': { fontSize: 34 } }}>{item.icon}</Box>
                  <Typography variant="subtitle2" sx={{ color: C.text, fontWeight: 700, fontFamily: '"Noto Serif TC", serif' }}>{item.title}</Typography>
                  <Typography variant="caption" sx={{ color: C.textLight }}>{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(25px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default TemplateHandmade;


